/**
 * テロップ design の選択肢カタログ (正本)。
 *
 * 編集 UI (CaptionEditor) が書ける値の空間と、AI 編集提案 (backend propose_service) が返してよい値の
 * 空間を一致させるための単一の定義。列挙 (配置・揃え・背景の形)、既定値、比率、丸めの刻み、AI 向けの
 * 上限をここに置き、フォント一覧は fonts/captionFonts.ts (それ自身が正本。フォントパック生成にも使う) を
 * 同梱する。設計: docs/features/video-editor-ai-caption-design-catalog-plan.md §3.5
 *
 * backend はここから生成した JSON (backend/services/video_edit/caption_catalog.json) を読む。
 * この TS を変えたら `npm run catalog:caption` で JSON を再生成してコミットすること
 * (鮮度は schema/__tests__/captionCatalog.test.ts が検証し、古いと CI が落ちる)。
 *
 * schema/project.ts の DEFAULT_CAPTION_* / 比率定数はここから再 export している (import 元は従来どおり
 * project.ts でよい)。
 */
import {
  CAPTION_FONT_AVAILABLE_WEIGHTS,
  CAPTION_FONT_CATEGORIES,
  CAPTION_FONT_CATEGORY_ORDER,
  CAPTION_FONT_DESCRIPTIONS,
  CAPTION_FONT_FAMILIES,
  CAPTION_FONT_PACK_NAME,
  CAPTION_FONT_POSTSCRIPT_FAMILY,
  DEFAULT_CAPTION_FONT_FAMILY,
  DEFAULT_CAPTION_FONT_WEIGHT,
  LEGACY_FAMILY_MAP,
} from '../fonts/captionFonts';
import { CAPTION_FONT_TAGS, CAPTION_FONT_TAG_AXES } from '../fonts/captionFontTags';

/**
 * カタログの形式バージョン。項目の追加・意味の変更で上げ、backend / レンダラーは対応バージョンを検証する。
 * v3 (2026-09-15): フォントのタグ tags と語彙 fontTagAxes を追加、等幅版 4 書体を削除
 * (docs/features/video-editor-caption-font-tags-plan.md)。
 * v4 (2026-09-24): 縦位置に topUpper (上とやや上の中間) / bottomLower (やや下と下の中間) と、上下帯
 * letterboxTop / letterboxBottom (中央の動画領域の上下の余白の中央。領域の比率は design.letterboxRatio) と、
 * その動画領域側の縁に寄せる letterboxTopInner (上の帯・下付き) / letterboxBottomInner (下の帯・上付き) を追加。
 * v5 (2026-10-19): 縦位置に upperCenter (やや上と中央の中間 37.5%) / lowerCenter (中央とやや下の中間 62.5%) を追加
 * (9:16 の縦の段を 12.5% 刻みの 9 段にする)。
 */
export const CAPTION_CATALOG_VERSION = 5;

// ── 列挙 ─────────────────────────────────────────────────────────

/**
 * 縦位置: 9 段階 (upper / lower は縦型動画のセーフゾーン用に、端と中央の中間。topUpper / bottomLower /
 * upperCenter / lowerCenter はさらにその間。UI では 9:16 のときだけ選べる) + 画面端に密着させる
 * header / footer (セーフゾーンを無視。全幅の帯を敷いた見出し・字幕用途) + 上下帯 letterboxTop /
 * letterboxBottom (中央に動画領域を想定した上下の余白の中央。schema/project.ts の captionAnchorRatio)。
 */
export const CAPTION_POSITIONS = [
  'top', 'topUpper', 'upper', 'upperCenter', 'center', 'lowerCenter', 'lower', 'bottomLower', 'bottom',
  'header', 'footer', 'letterboxTop', 'letterboxBottom', 'letterboxTopInner', 'letterboxBottomInner',
] as const;
export type CaptionPosition = (typeof CAPTION_POSITIONS)[number];
/** セーフゾーンを無視して画面の端に密着させる配置。align は center 固定、背景は band / none のみ。 */
export const CAPTION_EDGE_POSITIONS = ['header', 'footer'] as const;
export const CAPTION_ALIGNS = ['left', 'center', 'right'] as const;
export type CaptionAlign = (typeof CAPTION_ALIGNS)[number];
/** band = 画面幅いっぱいの帯、box = 文字にフィットした箱 (1 行ごと)、none = なし。 */
export const CAPTION_BACKGROUNDS = ['band', 'box', 'none'] as const;
export type CaptionBackground = (typeof CAPTION_BACKGROUNDS)[number];
/**
 * 縁取り (outlineWidthPx / outline2WidthPx) の角の形。round = 丸 (text-shadow のリング / lineJoin round)、
 * miter = 角 (-webkit-text-stroke / lineJoin miter)。位置は常に外側 (文字の縁から外に太さぶん。
 * センター/インナーは提供しない: ブラウザ描画で丸 × センターを本来の形で作れず、複雑になるため)。
 */
export const CAPTION_OUTLINE_JOINS = ['round', 'miter'] as const;
export type CaptionOutlineJoin = (typeof CAPTION_OUTLINE_JOINS)[number];

// ── 既定値 (design 未指定のテロップに UI が表示する値 = AI 出力の欠落時の値) ──

export const CAPTION_DEFAULTS = {
  fontFamily: DEFAULT_CAPTION_FONT_FAMILY,
  fontWeight: DEFAULT_CAPTION_FONT_WEIGHT,
  /** 横型の既定フォントサイズ。 */
  fontSizePx: 46,
  /** 縦型・正方形 (高さ >= 幅) の既定フォントサイズ。横型より一回り大きく (1080 幅で約 5.5%)。 */
  fontSizePortraitPx: 60,
  color: '#FFFFFF',
  /** 縁取りの既定色 (濃いグレー)。 */
  outlineColor: '#333333',
  /** 2 重縁取りの外縁の既定色 (白)。 */
  outline2Color: '#FFFFFF',
  /** 縁取りの角の形の既定 (丸)。従来の描画と同じ。 */
  outlineJoin: 'round',
  /** 帯 / 箱の既定色 (#RRGGBB) と不透明度。合成すると #000000A6 = レンダラー既定の rgba(0,0,0,0.65)。 */
  bandRgb: '#000000',
  bandOpacity: 0.65,
  shadowColor: '#000000',
  shadowOpacity: 0.75,
  background: 'none',
  positionPortrait: 'lower',
  positionLandscape: 'bottom',
  align: 'center',
} as const satisfies {
  background: CaptionBackground;
  positionPortrait: CaptionPosition;
  positionLandscape: CaptionPosition;
  align: CaptionAlign;
  outlineJoin: CaptionOutlineJoin;
  [key: string]: string | number;
};

// ── 比率 (フォントサイズ比などの自動値) ───────────────────────────

export const CAPTION_RATIOS = {
  /** 縁取りの自動の太さ (フォントサイズ比)。横型は細め、縦型はスマホ全画面で背景が賑やかなので太め。 */
  outlineWidthLandscape: 0.05,
  outlineWidthPortrait: 0.1,
  /** 影のぼかしの既定 (影を有効にしたときの初期値)。 */
  shadowBlur: 0.2,
  /** 影の距離 (offset 未指定時)。Premiere の「距離 10」相当の控えめな移動。 */
  shadowDistance: 0.1,
  /** 影の角度 (Premiere 流: 0° = 上、時計回り。135° = 右下)。 */
  shadowAngleDeg: 135,
  /** 帯 (band) の上下余白の既定。 */
  bandPadding: 0.5,
  /** 箱 (box) の角丸の既定。 */
  boxRadius: 0.15,
} as const;

// ── 刻み (UI のステッパー / スライダーと AI 正規化の丸め単位) ──────

export const CAPTION_STEPS = { px: 1, opacity: 0.01 } as const;

// ── AI 向けの上限 (UI には上限が無い。UI ⊇ AI) ─────────────────────

export const CAPTION_AI_LIMITS = {
  /** UI のサイズステッパーの最小値と同じ。 */
  minFontSizePx: 8,
  /** fontSizePx の上限 = max(floor, round(画面高 × ratio))。 */
  fontSizeMaxHeightRatio: 0.2,
  fontSizeMaxFloorPx: 160,
  /** outlineWidthPx / outline2WidthPx の上限。 */
  outlineMaxHeightRatio: 0.025,
  outlineMaxFloorPx: 12,
  /** shadowOffsetXPx / YPx の上限 (±)。 */
  shadowOffsetMaxHeightRatio: 0.05,
  shadowOffsetMaxFloorPx: 30,
  shadowSizeMaxPx: 40,
  shadowBlurMaxPx: 40,
  /** backgroundPaddingPx の上限 = round(画面高 × ratio)。backgroundRadiusPx の上限は fontSizePx。 */
  backgroundPaddingMaxHeightRatio: 0.2,
} as const;

// ── 丸め (frontend / backend で同じ式) ─────────────────────────────

/**
 * JavaScript の Math.round と同じ half-up 丸め (2.5 → 3、−2.5 → −2) を刻み step で行う。
 * バックエンドの AI 提案の正規化 (propose_service._round_to_step) と同じ式にして、frontend と backend で
 * 境界値の結果を一致させる (共通テストベクトル backend/tests/fixtures/caption_design_rounding_vectors.json)。
 */
export function roundToStep(value: number, step = 1): number {
  // step の逆数を掛けて floor し、逆数で割る (× step だと 57 × 0.01 = 0.5700000000000001 のような値になる)
  const inv = 1 / step;
  return Math.floor(value * inv + 0.5) / inv;
}

/** 不透明度 (0〜1) を 8bit アルファの 16 進 2 桁 (大文字) にする。範囲外はクランプ。 */
export function opacityToAlphaHex(opacity: number): string {
  const a = Math.round(Math.min(1, Math.max(0, opacity)) * 255);
  return a.toString(16).padStart(2, '0').toUpperCase();
}

// ── JSON (backend が読む生成物) ────────────────────────────────────

export interface CaptionCatalogFont {
  family: string;
  category: string;
  description: string;
  /** 提供ウェイト (昇順・重複なし)。 */
  weights: number[];
  /** フォント ZIP の名前 (scripts/fonts/build_caption_font_packs.py が使う)。 */
  packName: string;
  /** 静的フォントの PostScript ファミリー名 (Premiere XML の fontname)。 */
  postScriptFamily: string;
  /** AI 編集の fontRequest.traits が選べるタグ (印象→用途→形状→世界観→感情の順)。 */
  tags: string[];
}

export interface CaptionCatalog {
  version: number;
  fontCategories: string[];
  fonts: CaptionCatalogFont[];
  /** タグの語彙 (5 軸)。AI プロンプトの分類別語彙生成に使う。 */
  fontTagAxes: { axis: string; tags: string[] }[];
  positions: string[];
  edgePositions: string[];
  aligns: string[];
  backgrounds: string[];
  outlineJoins: string[];
  defaults: typeof CAPTION_DEFAULTS;
  /**
   * 旧スキーマ (種別指定) / 旧許可リスト (macOS 標準フォント名) → 許可リストのフォント名。
   * 許可リストに無い名前は既定フォント (defaults.fontFamily) に落とす。
   * backend の caption_catalog.resolve_caption_font_family がこの表を読む
   * (以前は手動コピーだったため、FE を変えても backend が追随しなかった)。
   */
  legacyFontFamilies: Record<string, string>;
  ratios: typeof CAPTION_RATIOS;
  steps: typeof CAPTION_STEPS;
  aiLimits: typeof CAPTION_AI_LIMITS;
}

export function buildCaptionCatalog(): CaptionCatalog {
  return {
    version: CAPTION_CATALOG_VERSION,
    fontCategories: [...CAPTION_FONT_CATEGORY_ORDER],
    fonts: CAPTION_FONT_FAMILIES.map((family) => ({
      family,
      category: CAPTION_FONT_CATEGORIES[family],
      description: CAPTION_FONT_DESCRIPTIONS[family],
      weights: [...CAPTION_FONT_AVAILABLE_WEIGHTS[family]],
      packName: CAPTION_FONT_PACK_NAME[family],
      postScriptFamily: CAPTION_FONT_POSTSCRIPT_FAMILY[family],
      tags: [...CAPTION_FONT_TAGS[family]],
    })),
    fontTagAxes: CAPTION_FONT_TAG_AXES.map(({ axis, tags }) => ({ axis, tags: [...tags] })),
    positions: [...CAPTION_POSITIONS],
    edgePositions: [...CAPTION_EDGE_POSITIONS],
    aligns: [...CAPTION_ALIGNS],
    backgrounds: [...CAPTION_BACKGROUNDS],
    outlineJoins: [...CAPTION_OUTLINE_JOINS],
    defaults: CAPTION_DEFAULTS,
    legacyFontFamilies: { ...LEGACY_FAMILY_MAP },
    ratios: CAPTION_RATIOS,
    steps: CAPTION_STEPS,
    aiLimits: CAPTION_AI_LIMITS,
  };
}

/** コミットする JSON の文字列 (2 スペース・末尾改行)。生成と鮮度検証で同じ関数を使う。 */
export function captionCatalogJson(): string {
  return `${JSON.stringify(buildCaptionCatalog(), null, 2)}\n`;
}
